
import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRef,
  watch,
  onMounted,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { LookupAPI } from "@/services/api/Lookup.service";
import { useToast } from "vue-toastification";
import LookupLang from "@/views/lookup/views/LookupLang.vue";
import { hasPermission } from "@/core/services/permission.service";
import {
  ILookup,
  ILookupDataModel,
  ILookupLangModel,
} from "@/interfaces/iLookup";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import * as Yup from "yup";
import {
  lookupDataModelTableBulkDelete,
  lookupDataModelTableFilters,
  lookupDataModelTableHeaders,
} from "../dataTableOptions";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";

export default defineComponent({
  props: {
    data: { type: Object as PropType<ILookup>, required: true },
    isActive: { type: Boolean, default: false },
  },
  components: { Datatable, VZDeleteModal, LookupLang },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const tableLookupDataModel = ref<null | HTMLFormElement>(null);
    const dataProp = toRef(props, "data");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const defaultFilterLookupDataModel = ref({ status: "ACTIVE" });
    const showDeleteDialog = ref<any>(false);
    const showDataModelSearch = true;
    const message = t("pages.lookup.lookupDataaModelDeleted");
    const lookupModelAddedMsg = t("pages.lookup.lookupDataaModelSuccess");
    const tableDataLookupLang = ref<ILookupLangModel[]>([]);
    const tableDataLookupDataModel = ref<ILookupDataModel[]>([]);
    const selectedForActionLookupModel = ref<Partial<ILookupDataModel>>({});
    const localPreference = ref({});
    const toast = useToast();
    const dataModelClickedItem = ref();
    let dataModelWebId = ref();
    const bulkDeleteLookupDataModel = ref<IBulkDelete>(
      lookupDataModelTableBulkDelete()
    );
    const tableHeaderLookupDataModel = ref(lookupDataModelTableHeaders());
    const tableFilterLookupDataModel = ref(lookupDataModelTableFilters());
    const format = computed(() => {
      return FormatMethods;
    });
    onMounted(async () => {
      // await queryUpdateLookupDataModel(dataProp.value.webId, {
      //   ...localPreference.value,
      // });
      loading.value = true;
    });
    const tableRowClick = async (item: ILookupDataModel) => {
      try {
        dataModelClickedItem.value = item;
        dataModelClickedItem.value = null;
        setTimeout(() => {
          dataModelClickedItem.value = item;
        }, 1000);
        dataModelWebId.value = item.webId;
        tableDataLookupLang.value = [];
        const response = await LookupAPI.getInstance().getLookupLangList(
          dataModelWebId.value,
          {
            ...localPreference.value,
          }
        );
        tableDataLookupLang.value = response.content;
      } finally {
        // TODO
      }
    };
    let permissionModel = ref<any>({
      create: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:CREATE",
      delete: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:DELETE",
      update: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:UPDATE",
    });
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdateLookupDataModel(dataProp.value.webId, params);
    };

    let formDataLookupDataModel = ref<IAddEditFormModel>({
      header: "LookupDatamodel",
      edit: false,
      data: null,
      label: [
        { addHeader: t("pages.lookup.addDataModel") },
        { editHeader: t("pages.lookup.editDataModel") },
      ],
      validationSchema: Yup.object({
        code: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("pages.lookup.code"), 30))
          .required(t("pages.lookup.code") + " " + t("common.isRequired"))
          .matches(/\S/, t("pages.lookup.code") + " " + t("common.isRequired")),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .nullable()
          .min(Yup.ref("startDate"), t("validation.greaterEndDate")),
        text: Yup.string()
          .strict(true)
          .max(100, getMaxLengthErrorMessage(t("pages.lookup.text"), 100))
          .required(t("pages.lookup.text") + " " + t("common.isRequired"))
          .matches(/\S/, t("pages.lookup.text") + " " + t("common.isRequired")),
        sorting: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("pages.lookup.sort"), 30))
          .matches(
            /^[0-9]+$/,
            t("pages.lookup.sort") + " " + t("validation.numericValidation")
          )
          .required(t("pages.lookup.sort") + " " + t("common.isRequired")),
      }),
      model: [
        {
          name: "code",
          label: t("pages.lookup.code"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "text",
          label: t("common.text"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "startDate",
          label: t("common.startDate"),
          type: "date",
          required: true,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "endDate",
          label: t("common.endDate"),
          type: "date",
          required: false,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "sorting",
          label: t("common.sort"),
          type: "text",
          value: "",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          fieldType: "number",
          placeholder: "0",
        },
        {
          name: "description",
          label: t("common.description"),
          type: "textarea",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
        },
      ],
    });
    const editLookupDatamodel = (item: ILookup) => {
      if (tableLookupDataModel.value) {
        tableLookupDataModel.value.openAddEditModal = true;
      }
      formDataLookupDataModel.value.data = { ...item };
      formDataLookupDataModel.value.edit = true;
    };
    const submitLookupDataModel = async (reqData) => {
      loading.value = true;
      reqData = {
        ...reqData,
        lookupId: dataProp.value.webId,
        status: "ACTIVE",
      };
      await LookupAPI.getInstance()
        .addLookupModelList(reqData, formDataLookupDataModel.value.edit)
        .then(() => {
          queryUpdateLookupDataModel(dataProp.value.webId, {
            ...localPreference.value,
          });
        })
        .catch(() => {
          //
        })
        .finally(() => {
          toast.success(lookupModelAddedMsg);
          loading.value = false;
        });
    };
    const queryUpdateLookupDataModel = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await LookupAPI.getInstance().getLookupModelList({
          ...params,
          lookupId: dataProp.value.webId,
        });
        localPreference.value = { ...params };
        tableDataLookupDataModel.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };
    const refreshListLookupDataModel = () => {
      showDeleteDialog.value = false;
      queryUpdateLookupDataModel(dataProp.value.webId, {
        ...localPreference.value,
      });
    };
    watch(
      () => props.isActive,
      async (newValue) => {
        if (newValue) {
          tableDataLookupDataModel.value = [];
          dataModelClickedItem.value = null;
          // queryUpdateLookupDataModel(dataProp.value.webId, {
          //   ...defaultFilterLookupDataModel.value,
          //   page: page.value - 1,
          //   size: rowsPerPage.value,
          // });
          loading.value = true;
          if (tableLookupDataModel.value?.VZFilter) {
            await tableLookupDataModel.value.VZFilter.getAppliedFilters();
          }
        }
      },
      { deep: true }
    );
    return {
      bulkDeleteLookupDataModel,
      format,
      tableDataLookupDataModel,
      tableHeaderLookupDataModel,
      tableFilterLookupDataModel,
      showDataModelSearch,
      page,
      total,
      dataProp,
      tableDataLookupLang,
      rowsPerPage,
      defaultFilterLookupDataModel,
      loading,
      formDataLookupDataModel,
      editLookupDatamodel,
      submitLookupDataModel,
      selectedForActionLookupModel,
      syncQuery,
      refreshListLookupDataModel,
      tableRowClick,
      dataModelClickedItem,
      hasPermission,
      permissionModel,
      tableLookupDataModel,
      showDeleteDialog,
      t,
    };
  },
});
